import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useWorkflowPlaceholderOptions } from "@/pages/workflows/edit/actions/use-workflow-placeholder-options.ts";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowUpdateBlockActionInvitation } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiInviteEmailType,
  ApiWorkflowBlockActionInvitation,
  ApiWorkflowBlockActionInvitationSave,
  getApiInviteEmailTypeSelectItems,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  Stack,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "./WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionInvitationModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionInvitation: ApiWorkflowBlockActionInvitation;
}

interface FormData extends WorkflowActionTimingFormFields {
  title: string | null;
  invite_email_type: ApiInviteEmailType;
  subject: string;
  body: string;
}

export const WorkflowEditActionInvitationModal = ({
  workflowVersion,
  block,
  actionInvitation,
  ...props
}: WorkflowEditActionInvitationModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const placeholderOptions = useWorkflowPlaceholderOptions(
    workflowVersion.trigger_event?.event_type,
    ["employee", "manager"]
  );
  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    invite_email_type: yup
      .mixed<ApiInviteEmailType>()
      .oneOf(Object.values(ApiInviteEmailType))
      .required(t("errors:field_required")),
    subject: yup.string().required(t("errors:field_required")),
    body: yup.string().required(t("errors:field_required")),
  });
  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      invite_email_type: actionInvitation.invite_email_type ?? ApiInviteEmailType.work,
      subject: actionInvitation.subject,
      body: actionInvitation.body,
    },
  });

  const updateBlockAction = useWorkflowUpdateBlockActionInvitation(control);

  const handleSave = handleSubmit(async (data) => {
    try {
      const blockData: ApiWorkflowBlockActionInvitationSave = {
        subject: data.subject,
        body: data.body,
        invite_email_type: data.invite_email_type,
      };

      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: blockData,
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.invitation,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const handleTabChanged = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_invitation")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              isLoading={updateBlockAction.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={handleTabChanged} activeTab={activeTab}>
            <TabItem title={t("common:message")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Controller
                  control={control}
                  name={"invite_email_type"}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label={t("core:email")}
                      emptyTitle={t("common:select")}
                      value={value}
                      required={true}
                      options={getApiInviteEmailTypeSelectItems()}
                      onChange={onChange}
                      error={errors.invite_email_type?.message}
                    />
                  )}
                />
                <InputWithVariableInsert
                  label={t("core:email_subject")}
                  type="text"
                  {...register("subject")}
                  required={true}
                  error={errors.subject?.message}
                  variableOptions={placeholderOptions}
                />
                <Stack>
                  <Title
                    header={t("workflow:action_type_invitation_body")}
                    size={"xs"}
                    paddingBottom={true}
                    required={true}
                  />
                  <Controller
                    control={control}
                    name={"body"}
                    render={({ field: { value, onChange } }) => (
                      <RichEditor
                        value={value ?? ""}
                        onChange={onChange}
                        error={errors.body?.message}
                        placeholderOptions={placeholderOptions}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />

                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
